import React, { useState, useEffect } from 'react';
import {
    Box, VStack, HStack, Text, Card, CardBody, Badge, Heading,
    Button, Select, Input, Image, Icon, Divider,
    Spinner, useToast, Flex, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText,

    Modal, ModalOverlay, ModalContent, ModalBody, ModalCloseButton, ModalFooter, ModalHeader, Textarea, useDisclosure
} from '@chakra-ui/react';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { FiCalendar, FiChevronLeft, FiChevronRight, FiImage, FiCheckCircle, FiXCircle, FiSmile } from 'react-icons/fi';
import { authAPI, resolveImageUrl } from '../services/api';
import LogoPulseLoader from '../components/LogoPulseLoader';
import { useAuth } from '../context/AuthContext';
import { filterClassesForStaffUser } from '../utils/teacherClassAccess';

const getDisplayLabel = (label) => {
    const map = {
        '小一': '一年級',
        '小二': '二年級',
        '小三': '三年級',
        '小四': '四年級',
        '小五': '五年級',
        '小六': '六年級',
        '全班': '全班事項',
        '英文': '英文作業',
        '個別': '個別提醒'
    };
    return map[label] || label;
};

import { getTodayDateString, formatTime } from '../utils/dateUtils';

const TeacherCommBookHistoryPage = () => {
    const { user } = useAuth();
    const toast = useToast();
    const [loading, setLoading] = useState(true);

    // Data State
    const [classes, setClasses] = useState([]);
    const [selectedClassId, setSelectedClassId] = useState('');
    const [selectedDate, setSelectedDate] = useState(getTodayDateString());
    const [bookData, setBookData] = useState(null);
    const [students, setStudents] = useState([]);

    // UI State
    const [selectedImage, setSelectedImage] = useState(null);
    const { isOpen: isSigOpen, onOpen: onSigOpen, onClose: onSigClose } = useDisclosure();

    // Reply Modal State
    const { isOpen: isReplyOpen, onOpen: onReplyOpen, onClose: onReplyClose } = useDisclosure();
    const [replyTargetId, setReplyTargetId] = useState(null);
    const [replyText, setReplyText] = useState("收到，謝謝！");

    // 1. Fetch Classes on Mount
    useEffect(() => {
        const fetchClasses = async () => {
            try {
                // Use getClasses instead of getClassesLite to ensure we get full data
                // getClassesLite might be restricted or implemented differently
                const res = await authAPI.getClasses();
                let myClasses = res.data;

                // If teacher, only show their own classes
                myClasses = filterClassesForStaffUser(myClasses, user);

                setClasses(myClasses);
                if (myClasses.length > 0) {
                    setSelectedClassId(myClasses[0].id);
                } else {
                    toast({ title: "尚無班級資料", status: "warning" });
                }
            } catch (error) {
                console.error("Error fetching classes:", error);
                toast({ title: "無法載入班級列表", status: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchClasses();
    }, [user, toast]);

    // 2. Fetch Book Data when Class or Date changes
    useEffect(() => {
        if (selectedClassId && selectedDate) {
            fetchBook();
        }
    }, [selectedClassId, selectedDate]);

    const fetchBook = async () => {
        setLoading(true);
        setBookData(null); // Reset while loading
        try {
            // Fetch Book Data (contains both book content and student list)
            const res = await authAPI.communicationBook.getByClass(selectedClassId, selectedDate);
            const data = res.data;

            setBookData(data.book || null);
            setStudents(data.students || []);
        } catch (error) {
            console.error("Error fetching book:", error);
            // It's possible there is no book for this date, handle gracefully
            setBookData(null);
            setStudents([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (offset) => {
        const d = new Date(selectedDate);
        d.setDate(d.getDate() + offset);
        setSelectedDate(d.toISOString().split('T')[0]);
    };

    const openLightbox = (url) => setSelectedImage(url);
    const closeLightbox = () => setSelectedImage(null);

    // Handle Teacher Acknowledge
    const openReplyModal = (sigId, currentReply) => {
        setReplyTargetId(sigId);
        setReplyText(currentReply || "收到，謝謝！");
        onReplyOpen();
    };

    const handleSendReply = async () => {
        if (!replyTargetId) return;

        try {
            const payload = { reply: replyText };
            await authAPI.communicationBook.markSignatureRead(replyTargetId, payload);

            // Update local state
            setBookData(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    signatures: prev.signatures.map(s =>
                        s.id === replyTargetId ? { ...s, teacher_read_at: new Date().toISOString(), teacher_reply: replyText } : s
                    )
                };
            });
            toast({ title: "已發送回覆", status: "success", duration: 1000 });
            onReplyClose();
        } catch (error) {
            console.error("Failed to send reply:", error);
            toast({ title: "發送失敗", status: "error" });
        }
    };

    // Calculate Signature Stats
    const totalStudents = students.length;
    const signedCount = bookData?.signatures?.length || 0;
    const signedStudentIds = new Set(bookData?.signatures?.map(s => s.student_id));

    return (
        <Box p={5} pb="100px" maxW="1200px" mx="auto">
            <VStack spacing={6} align="stretch">
                <Heading size="lg" color="brand.800">聯絡簿紀錄</Heading>


                {/* Header & Controls */}
                <Card shadow="sm" borderRadius="xl">
                    <CardBody>
                        <Flex direction={{ base: 'column', md: 'row' }} gap={4} justify="space-between" align="center">

                            {/* Class Selector */}
                            <HStack w={{ base: '100%', md: 'auto' }}>
                                <Text fontWeight="bold" whiteSpace="nowrap">班級：</Text>
                                <Select
                                    value={selectedClassId}
                                    onChange={(e) => setSelectedClassId(e.target.value)}
                                    maxW="200px"
                                >
                                    {classes.map(cls => (
                                        <option key={cls.id} value={cls.id}>{cls.name}</option>
                                    ))}
                                </Select>
                            </HStack>

                            {/* Date Picker */}
                            <HStack w={{ base: '100%', md: 'auto' }}>
                                <Button onClick={() => handleDateChange(-1)} size="sm"><FiChevronLeft /></Button>
                                <Input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    maxW="160px"
                                />
                                <Button onClick={() => handleDateChange(1)} size="sm"><FiChevronRight /></Button>
                            </HStack>

                        </Flex>
                    </CardBody>
                </Card>

                {/* Main Content */}
                {loading ? (
                    <Flex justify="center" p={10}><LogoPulseLoader /></Flex>
                ) : !bookData ? (
                    <Flex direction="column" align="center" justify="center" py={20} color="gray.400" bg="white" borderRadius="xl" shadow="sm">
                        <Icon as={FiCalendar} boxSize={16} mb={4} />
                        <Text fontSize="xl">本日無聯絡簿紀錄</Text>
                        <Text fontSize="md">請選擇其他日期或確認是否已發送。</Text>
                    </Flex>
                ) : (
                    <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6}>

                        {/* Left: Book Content (Preview) */}
                        <Box gridColumn={{ lg: "span 2" }}>
                            <Card borderRadius="2xl" shadow="md" overflow="hidden">
                                <Box bg="brand.500" p={4} color="white">
                                    <HStack justify="space-between">
                                        <Text fontSize="xl" fontWeight="bold">
                                            {new Date(selectedDate).toLocaleDateString('zh-TW', { month: '2-digit', day: '2-digit' })} 聯絡簿內容
                                        </Text>
                                        <Badge
                                            colorScheme={(bookData.is_sent || (bookData.signatures && bookData.signatures.length > 0)) ? "green" : "orange"}
                                            bg="white"
                                            color="brand.600"
                                            fontSize="md"
                                            px={2}
                                        >
                                            {(bookData.is_sent || (bookData.signatures && bookData.signatures.length > 0)) ? "已發送 (公開)" : "草稿 (未發送)"}
                                        </Badge>
                                    </HStack>
                                </Box>
                                <CardBody p={6}>
                                    <VStack align="stretch" spacing={6}>
                                        {/* Content Blocks */}
                                        {bookData.content && bookData.content.map((block, idx) => (
                                            <Box key={idx}>
                                                <Badge mb={2} colorScheme="blue" fontSize="md" variant="subtle">
                                                    {getDisplayLabel(block.label)}
                                                </Badge>
                                                <Text fontSize="lg" lineHeight="1.8" color="gray.800" whiteSpace="pre-wrap" pl={2} borderLeft="4px solid" borderColor="blue.100">
                                                    {block.text}
                                                </Text>
                                            </Box>
                                        ))}

                                        {/* Photos */}
                                        {bookData.photos && bookData.photos.length > 0 && (
                                            <Box pt={4}>
                                                <Divider mb={4} />
                                                <HStack mb={2} color="gray.600">
                                                    <Icon as={FiImage} />
                                                    <Text fontWeight="bold">附件照片</Text>
                                                </HStack>
                                                <HStack spacing={4} overflowX="auto" py={2}>
                                                    {bookData.photos.map((url, idx) => (
                                                        <Image
                                                            key={idx}
                                                            src={resolveImageUrl(url)}
                                                            h="150px"
                                                            borderRadius="lg"
                                                            cursor="pointer"
                                                            transition="transform 0.2s"
                                                            _hover={{ transform: 'scale(1.02)' }}
                                                            onClick={() => openLightbox(url)}
                                                        />
                                                    ))}
                                                </HStack>
                                            </Box>
                                        )}
                                    </VStack>
                                </CardBody>
                            </Card>
                        </Box>

                        {/* Right: Stats & Signatures */}
                        <VStack spacing={6} align="stretch">
                            <Card borderRadius="xl" shadow="sm">
                                <CardBody>
                                    <Stat>
                                        <StatLabel fontSize="lg" color="gray.500">簽名進度</StatLabel>
                                        <HStack align="baseline" spacing={2}>
                                            <StatNumber fontSize="4xl" color="brand.500">{signedCount}</StatNumber>
                                            <StatHelpText fontSize="xl">/ {totalStudents} 人</StatHelpText>
                                        </HStack>
                                        <Button
                                            mt={4}
                                            w="100%"
                                            variant="outline"
                                            colorScheme="brand"
                                            onClick={onSigOpen}
                                            disabled={signedCount === 0}
                                        >
                                            查看已簽名學生
                                        </Button>
                                    </Stat>
                                </CardBody>
                            </Card>

                            {/* Unsigned List (Quick View) */}
                            <Card borderRadius="xl" shadow="sm" flex={1}>
                                <CardBody>
                                    <Text fontWeight="bold" mb={4} color="gray.600">未簽名名單 ({totalStudents - signedCount})</Text>
                                    <SimpleGrid columns={2} spacing={2}>
                                        {students.filter(s => !signedStudentIds.has(s.id)).map(s => (
                                            <HStack key={s.id} bg="red.50" p={2} borderRadius="md">
                                                <Icon as={FiXCircle} color="red.400" />
                                                <Text fontSize="sm">{s.full_name}</Text>
                                            </HStack>
                                        ))}
                                    </SimpleGrid>
                                    {students.length === 0 && <Text color="gray.400">目前無學生資料</Text>}
                                </CardBody>
                            </Card>
                        </VStack>

                    </SimpleGrid>
                )}
            </VStack>

            {/* Signature List Modal */}
            <Modal isOpen={isSigOpen} onClose={onSigClose} scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalCloseButton />
                    <ModalBody p={6}>
                        <Text fontSize="xl" fontWeight="bold" mb={4}>已簽名學生列表</Text>
                        <VStack align="stretch" spacing={3}>
                            <VStack align="stretch" spacing={3}>
                                {bookData?.signatures?.map((sig, idx) => {
                                    const student = students.find(s => s.id === sig.student_id);
                                    return (
                                        <Box key={idx} bg="green.50" p={3} borderRadius="md" borderLeft="4px solid" borderColor="green.400">
                                            <HStack justify="space-between">
                                                <HStack>
                                                    <Icon as={FiCheckCircle} color="green.500" />
                                                    <Text fontWeight="bold">{student?.full_name || `學生#${sig.student_id}`}</Text>
                                                </HStack>
                                                <Text fontSize="xs" color="gray.500">
                                                    {formatTime(sig.signed_at)}
                                                </Text>
                                            </HStack>
                                            {sig.comment && (
                                                <Flex mt={2} align="center" justify="space-between" color="gray.600" bg="white" p={2} borderRadius="sm">
                                                    <HStack>
                                                        <Icon as={FiSmile} mt={1} color="orange.400" />
                                                        <Text fontSize="sm">{sig.comment}</Text>
                                                    </HStack>
                                                    {/* Teacher Acknowledgement Button */}
                                                    <Button
                                                        size="xs"
                                                        colorScheme={sig.teacher_reply ? "green" : (sig.teacher_read_at ? "gray" : "brand")}
                                                        variant={sig.teacher_read_at ? "outline" : "solid"}
                                                        onClick={() => openReplyModal(sig.id, sig.teacher_reply)}
                                                    >
                                                        {sig.teacher_reply ? "已回覆" : (sig.teacher_read_at ? "已讀/回覆" : "收到")}
                                                    </Button>
                                                </Flex>
                                            )}
                                        </Box>
                                    );
                                })}
                            </VStack>
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Reply Input Modal */}
            <Modal isOpen={isReplyOpen} onClose={onReplyClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>回覆家長</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text mb={2} fontSize="sm" color="gray.600">請輸入回覆內容 (預設為收到，謝謝！)</Text>
                        <Textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="請輸入回覆..."
                            rows={3}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onReplyClose}>取消</Button>
                        <Button colorScheme="brand" onClick={handleSendReply}>發送回覆</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Lightbox Modal */}
            <Modal isOpen={!!selectedImage} onClose={closeLightbox} size="full" isCentered>
                <ModalOverlay bg="blackAlpha.900" />
                <ModalContent bg="transparent" boxShadow="none" m={0}>
                    <ModalCloseButton
                        color="white"
                        bg="blackAlpha.600"
                        _hover={{ bg: "blackAlpha.800" }}
                        borderRadius="full"
                        zIndex={200}
                        size="lg"
                        position="fixed"
                        top="calc(env(safe-area-inset-top) + 12px)"
                        right="16px"
                    />
                    <ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
                        {selectedImage && (
                            <TransformWrapper doubleClick={{ mode: "reset" }}>
                                <TransformComponent
                                    wrapperStyle={{ width: "100vw", height: "100vh" }}
                                    contentStyle={{ width: "100vw", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}
                                >
                                    <Image src={selectedImage} w="100vw" maxH="100vh" objectFit="contain" />
                                </TransformComponent>
                            </TransformWrapper>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default TeacherCommBookHistoryPage;
